import { useState, useEffect } from "react";
import { Modal, Form, Input, Select, Upload, Button, Image, message, Row, Col, } from "antd";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { UploadOutlined } from "@ant-design/icons";
import callApi from "../../constants/API/CallApi";
import { ShowToast, Severty } from "../../helper/toast";
import { uploadFileToS3 } from "../../helper/uploadImage";
import { getBranches, getClasses, getContent, updateContent, getBelts, } from "../../constants/API/API";
import useApi from "../../hooks/useApi";

const { Option } = Select;
// done
const allowedTypes = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "application/pdf",
  "video/mp4",
  "video/quicktime",
  "video/x-m4v",
  "video/webm",
  "video/mov",
];

const ContentUpdate = ({
  editVisible,
  setEditVisible,
  selectedContentId,
  refresh,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [videoLoading, setVideoLoading] = useState(false);
  const [thumbnailLoading, setThumbnailLoading] = useState(false);

  const [contentData, setContentData] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [thumbList, setThumbList] = useState([]);
  const [description, setDescription] = useState("");
  const [classList, setClassList] = useState([]);
  const [branches, setBranches] = useState([]);
  const [belts, setBelts] = useState([]);
  const [contentType, setContentType] = useState("");
  const [selectedBelt, setSelectedBelt] = useState(null);
  const [selectedBranch, setSelectedBranch] = useState(null);


  useApi({
    callback: getBelts,
    payload: selectedBranch ? { branchId: selectedBranch } : null,
    dependencies: [selectedBranch],
    shouldCallApi: !!selectedBranch,
    onSuccess: ({ data }) => {
      setBelts(data || []);
    },
  });


  useApi({
    callback: getClasses,
    dependencies: [],
    shouldCallApi: true,
    onSuccess: ({ data }) => setClassList(data || []),
  });

  useApi({
    callback: getBranches,
    dependencies: [refresh],
    shouldCallApi: true,
    onSuccess: ({ data }) => {
      const active = (data || []).filter((b) => b.isActive);
      setBranches(active);
    },
  });

  // Fetch content by ID
  useEffect(() => {
    if (selectedContentId && editVisible) {
      callApi({
        callback: () => getContent(selectedContentId),
        payload: selectedContentId,
        onSuccess: ({ data }) => {
          setContentData(data);
          setContentType(data?.contentType);
          setSelectedBranch(data?.branchId);

          setSelectedBelt(data?.beltId || null);

          form.setFieldsValue({
            ...data,
            class: data?.class,
            beltId: data?.beltId,
          });

          setDescription(data?.description);

          if (data?.src) {
            setFileList([
              {
                uid: "1",
                name: "uploaded file",
                status: "done",
                url: data?.src,
              },
            ]);
          }

          if (data?.thumbnail) {
            setThumbList([
              {
                uid: "2",
                name: "thumbnail",
                status: "done",
                url: data?.thumbnail,
              },
            ]);
          }
        },
      });
    }
  }, [selectedContentId, editVisible]);

  const handleCancel = () => {
    setEditVisible(false);
    form.resetFields();
    setFileList([]);
    setThumbList([]);
    setDescription("");
    setContentType("");
    setSelectedBelt(null);
  };

  const beforeUpload = (file) => {
    if (!allowedTypes.includes(file.type)) {
      message.error("Unsupported file type.");
      return Upload.LIST_IGNORE;
    }
    return true;
  };

  const handleUpload = async (info, field) => {
    const { file } = info;
    if (field === "src") {
      setVideoLoading(true);
    } else {
      setThumbnailLoading(true);
    }

    try {
      const data = await uploadFileToS3({
        file,
        dirName: "content",
        bucketName: "invent-co-new",
      });

      const uploadedData = {
        uid: file.uid,
        name: file.name,
        status: "done",
        url: data.Location,
        thumbUrl: data.Location,
      };

      if (field === "src") {
        setFileList([uploadedData]);
        form.setFieldsValue({
          src: uploadedData.url,
        });
      } else {
        setThumbList([uploadedData]);
        form.setFieldsValue({ thumbnail: uploadedData.url });
      }
    } finally {
      if (field === "src") {
        setVideoLoading(false);
      } else {
        setThumbnailLoading(false);
      }
    }
  };

  const handleRemove = (field) => {
    if (field === "src") {
      setFileList([]);
      form.setFieldsValue({ src: null });
    } else {
      setThumbList([]);
      form.setFieldsValue({ thumbnail: null });
    }
  };

  const onFinish = (values) => {
    if (!description.trim()) {
      return message.error("Enter valid description.");
    }

    const payload = {
      ...values,
      description: description.trimStart(),
      src: fileList.length ? fileList[0].url : null,
      thumbnail: thumbList.length ? thumbList[0].url : null,
      ...(contentType === "video" && { beltId: selectedBelt }),
    };

    setLoading(true);

    callApi({
      callback: updateContent,
      payload: [payload, selectedContentId],
      onSuccess: () => {
        ShowToast("Content updated successfully", Severty.SUCCESS);
        refresh(Date.now());
        setEditVisible(false);
        setLoading(false);
      },
      onError: (err) => {
        ShowToast(err, Severty.ERROR);
        setLoading(false);
      },
    });
  };

  return (
    <Modal
      width={800}
      centered
      open={editVisible}
      title="Update Content"
      className="tab_modal"
      okButtonProps={{
        htmlType: "submit",
        form: "edit-content",
        loading,
        disabled: videoLoading || thumbnailLoading,
      }}
      onCancel={handleCancel}
    >
      <Form form={form} id="edit-content" onFinish={onFinish} layout="vertical">
        <Row gutter={12}>
          <Col span={24} md={12}>
            <Form.Item
              normalize={(val) => val?.trimStart()}
              name="title"
              label="Title"
              rules={[{ required: true }]}
            >
              <Input placeholder="Enter title" />
            </Form.Item>
          </Col>

          <Col span={24} md={12}>
            <Form.Item
              name="contentType"
              label="Content Type"
              rules={[{ required: true }]}
            >
              <Select
                placeholder="Select type"
                onChange={(v) => setContentType(v)}
              >
                <Option value="info">Informational</Option>
                <Option value="video">Training Video</Option>
              </Select>
            </Form.Item>
          </Col>


          <Col span={24} md={12}>
            <Form.Item
              name="branchId"
              label="Branch"
              rules={[{ required: true }]}
            >
              <Select placeholder="Select Branch"
                onChange={(value) => {
                  setSelectedBranch(value);
                  setBelts([]);
                  setSelectedBelt(null);
                  form.setFieldsValue({ beltId: undefined });
                }}
              >
                {branches?.map((b) => (
                  <Option key={b._id} value={b._id}>
                    {b.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {contentType === "video" && (
            <Col span={24} md={12}>
              <Form.Item
                name="beltId"
                label="Select Belt"
                rules={[{ required: true, message: "Please select a belt!" }]}
              >
                <Select
                  placeholder="Select Belt"
                  onChange={(val) => setSelectedBelt(val)}
                >
                  {belts?.map((belt) => (
                    <Option key={belt._id} value={belt._id}>
                      {belt.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}


          <Col span={24} md={12}>
            <Form.Item name="class" label="Class (Optional)">
              <Select mode="multiple" placeholder="Select class">
                {classList?.map((cls) => (
                  <Option key={cls._id} value={cls._id}>
                    {cls.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {/* Video Upload */}
          <Col span={24} md={12}>
            {" "}
            <Form.Item name="src" label="Upload Content">
              <Upload
                accept="video/*"
                className="video-uploaded"
                maxCount={1}
                beforeUpload={beforeUpload}
                fileList={fileList}
                customRequest={(info) => handleUpload(info, "src")}
                onRemove={() => handleRemove("src")}
              >
                {fileList.length < 1 && (
                  <Button icon={<UploadOutlined />} loading={videoLoading}>Upload</Button>
                )}
              </Upload>
            </Form.Item>
          </Col>

          {/* Thumbnail */}
          <Col span={24} md={12}>
            <Form.Item name="thumbnail" label="Upload Thumbnail">
              <Upload
                accept="image/*"
                listType="picture"
                maxCount={1}
                beforeUpload={beforeUpload}
                fileList={thumbList}
                customRequest={(info) => handleUpload(info, "thumbnail")}
                onRemove={() => handleRemove("thumbnail")}
              >
                {thumbList.length === 0 && (
                  <Button icon={<UploadOutlined />} loading={thumbnailLoading}>Upload Thumbnail</Button>
                )}
              </Upload>

              {thumbList.length > 0 && (
                <Image width={100} src={thumbList[0].url} className="mt-2" />
              )}
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item label="Description">
              <ReactQuill value={description} onChange={setDescription} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default ContentUpdate;
